import React, { useCallback, useEffect, useMemo, useState } from "react";

import useStore from "../../../services/Inventory/useStore";
import NoIndex from "../../../components/NoIndex";

const Stores = () => {
  const { getStores, getStoreItems, updateItemStatus } = useStore();

  const [stores, setStores] = useState([]);
  const [activeStoreId, setActiveStoreId] = useState("");

  const [items, setItems] = useState([]);

  const [search, setSearch] = useState("");

  /*
    selectedItems structure:

    {
      "11264": true,
      "11265": true
    }

    IMPORTANT:
    item_id duplicate ho sakta hai,
    isliye pk_item_id / size_id ko selection key use kar rahe hain.
  */
  const [selectedItems, setSelectedItems] = useState({});

  const [loading, setLoading] = useState(false);
  const [storeLoading, setStoreLoading] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const [actionStatus, setActionStatus] = useState("");

  const [submitLoading, setSubmitLoading] = useState(false);

  /*
   * Unique item identifier
   *
   * Priority:
   * pk_item_id
   * size_id
   * item_id
   */
  const getItemKey = useCallback((item) => {
    return String(item?.pk_item_id || item?.size_id || item?.item_id || "");
  }, []);

  /*
   * LOAD STORES
   */
  const loadStores = useCallback(async () => {
    try {
      setStoreLoading(true);

      const data = await getStores();

      setStores(Array.isArray(data) ? data : []);

      if (Array.isArray(data) && data.length > 0) {
        setActiveStoreId((previous) => {
          /*
           * Refresh ke case me current selected store
           * available hai toh wahi maintain karo.
           */
          const exists = data.some(
            (store) => String(store.fk_userid) === String(previous),
          );

          if (previous && exists) {
            return previous;
          }

          return String(data[0].fk_userid);
        });
      } else {
        setActiveStoreId("");
      }
    } catch (error) {
      console.error("Load stores error:", error);

      setStores([]);
    } finally {
      setStoreLoading(false);
    }
  }, [getStores]);

  /*
   * LOAD ITEMS
   */
  const loadStoreItems = useCallback(
    async (storeId) => {
      if (!storeId) {
        setItems([]);
        return;
      }

      try {
        setLoading(true);

        const data = await getStoreItems(storeId);

        console.log("getStoreItems response:", data);

        setItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Load store items error:", error);

        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [getStoreItems],
  );

  /*
   * INITIAL LOAD
   */
  useEffect(() => {
    loadStores();
  }, [loadStores]);

  /*
   * LOAD ITEMS WHEN STORE CHANGES
   */
  useEffect(() => {
    if (!activeStoreId) {
      setItems([]);
      return;
    }

    /*
     * Store change hone par old selection hatao.
     */
    setSelectedItems({});
    setSearch("");

    loadStoreItems(activeStoreId);
  }, [activeStoreId, loadStoreItems]);

  /*
   * SEARCH FILTER
   */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) {
      return items;
    }

    return items
      .map((group) => {
        const groupItems = Array.isArray(group?.item_list)
          ? group.item_list
          : [];

        const filteredItems = groupItems.filter((item) => {
          const itemId = String(item?.item_id || "").toLowerCase();

          const pkItemId = String(item?.pk_item_id || "").toLowerCase();

          const sizeId = String(item?.size_id || "").toLowerCase();

          const itemName = String(item?.item_name || "").toLowerCase();

          const description = String(
            item?.item_description || "",
          ).toLowerCase();

          const size = String(item?.size || "").toLowerCase();

          const groupName = String(group?.message || "").toLowerCase();

          return (
            groupName.includes(q) ||
            itemName.includes(q) ||
            description.includes(q) ||
            itemId === q ||
            pkItemId === q ||
            sizeId === q ||
            size.includes(q)
          );
        });

        return {
          ...group,
          item_list: filteredItems,
        };
      })
      .filter(
        (group) => Array.isArray(group.item_list) && group.item_list.length > 0,
      );
  }, [items, search]);

  /*
   * SINGLE ITEM SELECT / UNSELECT
   */
  const toggleItem = (item) => {
    const key = getItemKey(item);

    if (!key) return;

    setSelectedItems((previous) => ({
      ...previous,

      [key]: !previous[key],
    }));
  };

  /*
   * SELECT / UNSELECT GROUP
   */
  const toggleGroup = (group) => {
    const groupItems = Array.isArray(group?.item_list) ? group.item_list : [];

    if (groupItems.length === 0) {
      return;
    }

    const allSelected = groupItems.every((item) => {
      const key = getItemKey(item);

      return Boolean(selectedItems[key]);
    });

    setSelectedItems((previous) => {
      const updated = {
        ...previous,
      };

      groupItems.forEach((item) => {
        const key = getItemKey(item);

        if (key) {
          updated[key] = !allSelected;
        }
      });

      return updated;
    });
  };

  /*
   * SELECTED ITEMS
   *
   * IMPORTANT:
   * API response me duplicate rows aa rahe hain.
   *
   * Isliye duplicate pk_item_id / size_id
   * ko remove kar rahe hain.
   */
  const selectedList = useMemo(() => {
    const selectedMap = new Map();

    filtered.forEach((group) => {
      const groupItems = Array.isArray(group?.item_list) ? group.item_list : [];

      groupItems.forEach((item) => {
        const key = getItemKey(item);

        if (key && selectedItems[key] && !selectedMap.has(key)) {
          selectedMap.set(key, item);
        }
      });
    });

    return Array.from(selectedMap.values());
  }, [filtered, selectedItems, getItemKey]);

  /*
   * ANY ITEM SELECTED?
   */
  const hasSelectedItems = selectedList.length > 0;

  /*
   * ACTIVE STORE
   */
  const activeStore = useMemo(() => {
    return stores.find(
      (store) => String(store.fk_userid) === String(activeStoreId),
    );
  }, [stores, activeStoreId]);

  /*
   * OPEN CONFIRM MODAL
   */
  const openConfirmModal = (status) => {
    if (!hasSelectedItems) {
      return;
    }

    setActionStatus(status);
    setShowConfirm(true);
  };

  /*
   * UPDATE STATUS
   */
 const handleSubmit = async () => {
   try {
     setSubmitLoading(true);

     const payload = {
       fk_userid: activeStoreId,
       status: actionStatus,
       itemlist: selectedList.map((item) => ({
         itemid: item.item_id,
       })),
     };

     console.log("UPDATE PAYLOAD:", payload);

     const response = await updateItemStatus(payload);

     console.log("UPDATE RESPONSE:", response);

     if (response?.result?.some((item) => item.result === "Y")) {
       setShowConfirm(false);
       setSelectedItems({});

       const updatedData = await getStoreItems(activeStoreId);

       setItems(updatedData);
     } else {
       console.error("Item update failed:", response);
     }
   } catch (error) {
     console.error("Update item status error:", error);
   } finally {
     setSubmitLoading(false);
   }
 };
  /*
   * REFRESH
   */
  const handleRefresh = async () => {
    try {
      setSelectedItems({});

      const storeData = await getStores();

      setStores(Array.isArray(storeData) ? storeData : []);

      if (activeStoreId) {
        await loadStoreItems(activeStoreId);
      }
    } catch (error) {
      console.error("Refresh error:", error);
    }
  };

  return (
    <>
      <div className="p-6 min-h-screen">
        <NoIndex />

        {/* HEADER */}
        <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-6 montserrat-font-1">
          <div>
            <h1 className="text-xl font-semibold">Instamart Store Items</h1>

            {activeStore && (
              <p className="text-xs text-gray-500 mt-1">
                {activeStore.organization_name}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {/* SEARCH */}
            <input
              className="border px-3 py-2 rounded-md text-sm w-72 outline-none focus:ring-2 focus:ring-gray-200"
              placeholder="Search group / item / item id / size..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            {/* REFRESH */}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading || submitLoading}
              className="border px-3 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
              title="Refresh"
            >
              ⟳
            </button>

            {/* ACTIVE */}
            <button
              type="button"
              disabled={!hasSelectedItems || submitLoading}
              onClick={() => openConfirmModal("Y")}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set Active
            </button>

            {/* INACTIVE */}
            <button
              type="button"
              disabled={!hasSelectedItems || submitLoading}
              onClick={() => openConfirmModal("N")}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set Inactive
            </button>
          </div>
        </div>

        {/* STORE LOADING */}
        {storeLoading && (
          <div className="mb-4 text-sm text-gray-500 montserrat-font-1">
            Loading stores...
          </div>
        )}

        {/* STORE CARDS */}
        <div className="flex gap-4 mb-6 flex-wrap montserrat-font-1">
          {stores.map((store) => {
            const active = String(store.fk_userid) === String(activeStoreId);

            return (
              <button
                type="button"
                key={store.fk_userid}
                onClick={() => setActiveStoreId(String(store.fk_userid))}
                className={`text-left cursor-pointer border rounded-xl p-4 w-56 transition ${
                  active
                    ? "border-green-500 bg-green-50 shadow-sm"
                    : "bg-white hover:bg-gray-50"
                }`}
              >
                <div className="font-semibold break-words">
                  {store.organization_name || "Store"}
                </div>

                <div className="text-xs text-gray-500 mt-1">
                  Store ID: {store.fk_userid}
                </div>

                <div className="text-xs text-gray-500">
                  Total Items: {store.total_items ?? 0}
                </div>
              </button>
            );
          })}
        </div>

        {/* NO STORES */}
        {!storeLoading && stores.length === 0 && (
          <div className="bg-white border rounded-xl py-16 text-center text-gray-500 montserrat-font-1">
            No stores found
          </div>
        )}

        {/* ITEMS LOADING */}
        {loading ? (
          <div className="bg-white border rounded-xl text-center py-16 montserrat-font-1">
            <div className="text-sm text-gray-500">Loading items...</div>
          </div>
        ) : activeStoreId && filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 bg-white border rounded-xl montserrat-font-1">
            <div className="text-base font-medium text-gray-500">
              No items found for this store
            </div>

            {search && (
              <div className="text-xs text-gray-400 mt-2">
                Try another search
              </div>
            )}
          </div>
        ) : (
          filtered.map((group, groupIndex) => {
            const groupItems = Array.isArray(group?.item_list)
              ? group.item_list
              : [];

            /*
             * selected count unique item keys
             */
            const uniqueSelected = new Set();

            groupItems.forEach((item) => {
              const key = getItemKey(item);

              if (key && selectedItems[key]) {
                uniqueSelected.add(key);
              }
            });

            const selectedCount = uniqueSelected.size;

            /*
             * Select-all status
             */
            const allSelected =
              groupItems.length > 0 &&
              groupItems.every((item) => {
                const key = getItemKey(item);

                return Boolean(selectedItems[key]);
              });

            return (
              <div
                key={`${group.message || "group"}-${group.id || groupIndex}`}
                className="bg-white border rounded-xl mb-6 overflow-hidden montserrat-font-1"
              >
                {/* GROUP HEADER */}
                <div className="flex justify-between items-center gap-4 p-4 border-b">
                  <div>
                    <h2 className="font-semibold">
                      {group.message || "Items"}
                    </h2>

                    <p className="text-xs text-gray-500 mt-1">
                      {groupItems.length} items • {selectedCount} selected
                    </p>
                  </div>

                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => toggleGroup(group)}
                    />
                    Select all
                  </label>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                  <div className="min-w-[1000px]">
                    {/* TABLE HEADER */}
                    <div className="grid grid-cols-[80px_80px_minmax(220px,2fr)_120px_120px_120px_100px_120px] text-xs font-medium text-gray-500 px-4 py-3 border-b bg-gray-50 montserrat-font-1">
                      <div>Select</div>

                      <div>Image</div>

                      <div>Name</div>

                      <div>Item ID</div>

                      <div>Size</div>

                      <div>Price</div>

                      <div>Stock</div>

                      <div>Status</div>
                    </div>

                    {/* ROWS */}
                    <div className="max-h-[480px] overflow-y-auto">
                      {groupItems.map((item, itemIndex) => {
                        const key = getItemKey(item);

                        /*
                         * Response mapping:
                         *
                         * Y = Active
                         * N = Inactive
                         */
                        const isActive =
                          String(item.inStock || "")
                            .trim()
                            .toUpperCase() === "Y";

                        /*
                         * API me duplicate rows bhi aa sakti hain,
                         * isliye React key me index bhi use kar rahe hain.
                         */
                        const rowKey = `${key}-${itemIndex}`;

                        return (
                          <div
                            key={rowKey}
                            className="grid grid-cols-[80px_80px_minmax(220px,2fr)_120px_120px_120px_100px_120px] items-center px-4 py-3 border-b last:border-b-0 text-sm hover:bg-gray-50 montserrat-font-1"
                          >
                            {/* SELECT */}
                            <div>
                              <input
                                type="checkbox"
                                checked={Boolean(selectedItems[key])}
                                onChange={() => toggleItem(item)}
                              />
                            </div>

                            {/* IMAGE */}
                            <div>
                              {item.photo ? (
                                <img
                                  src={item.photo}
                                  alt={item.item_name || "Item"}
                                  className="w-12 h-12 rounded-md object-cover border bg-gray-50"
                                  loading="lazy"
                                  onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center text-[10px] text-gray-400 rounded-md">
                                  No Img
                                </div>
                              )}
                            </div>

                            {/* NAME */}
                            <div className="min-w-0 pr-4">
                              <div className="font-medium text-gray-900 break-words">
                                {item.item_name || "-"}
                              </div>

                              {item.item_description && (
                                <div className="text-xs text-gray-400 break-words mt-1 line-clamp-2">
                                  {item.item_description}
                                </div>
                              )}

                              <div className="text-[10px] text-gray-400 mt-1">
                                PK: {item.pk_item_id || "-"}
                              </div>
                            </div>

                            {/* ITEM ID */}
                            <div className="break-all">
                              {item.item_id || "-"}
                            </div>

                            {/* SIZE */}
                            <div>{item.size || "-"}</div>

                            {/* PRICE */}
                            <div>₹{Number(item.price || 0).toFixed(2)}</div>

                            {/* STOCK */}
                            <div>{item.stock ?? "-"}</div>

                            {/* STATUS */}
                            <div>
                              <span
                                className={`inline-flex whitespace-nowrap text-xs font-semibold px-2.5 py-1 rounded-full ${
                                  isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {isActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* CONFIRM MODAL */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 montserrat-font-1">
            <div className="bg-white w-full max-w-[460px] rounded-xl shadow-xl overflow-hidden">
              {/* MODAL HEADER */}
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="font-semibold text-lg">
                  {actionStatus === "Y"
                    ? "Confirm Set Active"
                    : "Confirm Set Inactive"}
                </h2>

                {!submitLoading && (
                  <button
                    type="button"
                    onClick={() => setShowConfirm(false)}
                    className="text-gray-400 hover:text-black text-xl"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* MODAL BODY */}
              <div className="p-4">
                <p className="text-sm mb-4 text-gray-700">
                  Are you sure you want to{" "}
                  <span className="font-semibold">
                    {actionStatus === "Y" ? "set active" : "set inactive"}
                  </span>{" "}
                  these selected items for{" "}
                  <span className="font-semibold">
                    {activeStore?.organization_name || "this store"}
                  </span>
                  ?
                </p>

                <div className="text-xs text-gray-500 mb-2">
                  {selectedList.length} item(s) selected
                </div>

                <div className="max-h-64 overflow-y-auto border rounded-lg bg-gray-50">
                  {selectedList.map((item) => {
                    const key = getItemKey(item);

                    return (
                      <div
                        key={key}
                        className="py-3 px-3 border-b last:border-none"
                      >
                        <div className="font-medium text-sm text-gray-900">
                          {item.item_name}
                        </div>

                        <div className="text-xs text-gray-500 mt-1">
                          Item ID: {item.item_id || "-"}
                        </div>

                        <div className="text-xs text-gray-500">
                          PK Item ID: {item.pk_item_id || "-"}
                        </div>

                        {item.size && (
                          <div className="text-xs text-gray-500">
                            Size: {item.size}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* MODAL FOOTER */}
              <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  disabled={submitLoading}
                  className="px-4 py-2 border bg-white rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={
                    submitLoading || !hasSelectedItems || !activeStoreId
                  }
                  className={`px-4 py-2 rounded-md text-sm text-white ${
                    actionStatus === "Y"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-500 hover:bg-red-600"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {submitLoading
                    ? "Updating..."
                    : actionStatus === "Y"
                      ? "Confirm Active"
                      : "Confirm Inactive"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Stores;
